import { ReactTFC } from "../../../../utils/fp/missing-ts-parts";
import { css } from "@emotion/react";
import { FONTS } from "../../../../styles/fonts";
import { CSS_MQ } from "../../../../styles/breakpoints";
import cn from "classnames";
import { ReactSVG } from "react-svg";
import React from "react";
import { CSS_COLORS } from "../../../../styles/styles-css-variables";

export const ButtonsListIconChoice: ReactTFC<{
  isChecked: boolean;
  onMakeChoice: () => void;
  icon:
    | {
        title: string;
        url: string;
      }
    | undefined;
  value: string;
  label: string;
  variant: "icon-focused" | "button-focused";
  onChoiceHovered: () => void;
}> = props => {
  const choiceStyle = css`
    &.variant-button-focused {
      &:hover,
      &.checked {
        ._wrapper {
          color: var(--anti-color);
          --text-color: var(--anti-color);
        }
      }

      &:hover:not(.checked) {
        ._wrapper {
          background-color: var(--color);
          opacity: 0.9;
        }
      }

      &.checked {
        ._wrapper {
          background-color: var(--color);
        }
      }

      ._wrapper {
        --h: 40px;
        --w: 80px;

        ${CSS_MQ.atLeastSm} {
          --w: 100px;
          --h: 50px;
        }

        grid-template-columns: var(--w) auto;
        grid-gap: 10px;

        border-width: 1px;
        border-style: solid;
        border-color: var(--color);
        background-color: rgb(0 0 0 / 10%);
        backdrop-filter: blur(8px) contrast(0.8);

        border-radius: 8px;

        ._label {
          color: var(--text-color);
          padding: 10px 10px 8px 0;

          ${CSS_MQ.atLeastMd} {
            padding: 18px 20px 16px 0;
          }

          line-height: 1.1;
          text-align: left;

          .no-icon & {
            grid-column: 1 / span 2;

            padding-left: 10px;

            ${CSS_MQ.atLeastMd} {
              padding-left: 20px;
            }
          }
        }
      }
    }

    &.variant-icon-focused {
      ${CSS_MQ.atLeastMd} {
        max-width: 25%;
      }

      ${CSS_MQ.atLeastLg} {
        max-width: 20%;
      }

      &:hover,
      &.checked {
        ._label {
          text-decoration: underline;
        }
      }

      ._wrapper {
        --w: 100px;
        --h: 50px;

        grid-gap: 2px;
        ${CSS_MQ.atLeastMd} {
          grid-gap: 10px;
        }

        ${CSS_MQ.atLeastSm} {
          --w: 150px;
          --h: 75px;
        }

        grid-template-rows: var(--h) auto;

        ._label {
          color: var(--text-color);

          line-height: 1.1;
          text-align: center;

          .no-icon & {
            grid-column: 1 / span 2;

            padding-left: 10px;

            ${CSS_MQ.atLeastMd} {
              padding-left: 20px;
            }
          }
        }
      }
    }

    ._wrapper {
      display: grid;
      align-items: center;

      cursor: pointer;

      ${FONTS.SwarovskiFutura500};

      text-transform: uppercase;

      font-size: 16px;
      color: ${CSS_COLORS.White};
      letter-spacing: 0.67px;

      --text-color: var(--color);

      min-height: var(--h);
    }

    &.checked {
      animation-name: scale-animation;
      animation-duration: 0.2s;
    }

    @keyframes scale-animation {
      0% {
        transform: scale(1);
      }

      25% {
        transform: scale(0.99);
      }

      100% {
        transform: scale(1);
      }
    }
  `;

  return (
    <button
      type={"button"}
      onClick={props.onMakeChoice}
      onMouseEnter={() => props.onChoiceHovered()}
      css={choiceStyle}
      className={cn(
        { checked: props.isChecked, "no-icon": !props.icon },
        props.variant === "button-focused" ? `variant-button-focused` : `variant-icon-focused`,
      )}
    >
      <div className={"_wrapper"}>
        {props.icon && (
          <ReactSVG
            src={props.icon.url}
            title={props.icon.title}
            alt={props.icon.title}
            css={css`
              height: var(--h);
              width: var(--w);

              justify-self: center;
              padding: 4px;

              svg {
                width: 100%;
                height: 100%;
                object-fit: contain;

                color: var(--text-color);
              }
            `}
          />
        )}
        <span className={"_label"}>{props.label}</span>
      </div>
    </button>
  );
};
